import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import AdminPage from './components/AdminPage';
import AttendancePage from './components/AttendancePage';
import AttendancePageBGD from './components/AttendancePageBGD';
import HistoryPage from './components/HistoryPage';
import LoginPage from './components/LoginPage';
import AdminLogin from './components/AdminLogin';
import DesktopBlockWarning from './components/DesktopBlockWarning';
import { isMobileDevice } from './utils/deviceDetection';
import { isBGDEmail } from './config/bgdEmails';
import { getGreeting } from './utils/greetings';
import './App.css';

// Component để sử dụng useNavigate
function AppContent({ employee, setEmployee, isAdminLoggedIn, setIsAdminLoggedIn }) {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    // Check device type on mount and when employee changes
    const isMobile = isMobileDevice();
    const isDesktopDevice = !isMobile;
    console.log('[App] Device check - isMobile:', isMobile, 'isDesktop:', isDesktopDevice, 'employee:', employee?.name);
    setIsDesktop(isDesktopDevice);
  }, [employee]);

  const isVIP = (emp) => isBGDEmail(emp?.email);

  // Nếu đang đăng nhập VIP nhưng user vào nhầm route (ví dụ vừa reload), tự điều hướng về /vip
  useEffect(() => {
    if (employee && isVIP(employee) && window.location.pathname === '/') {
      navigate('/vip', { replace: true });
    }
  }, [employee, navigate]);

  const handleEmployeeLogin = (emp) => {
    setEmployee(emp);
    localStorage.setItem('chamcong_employee', JSON.stringify(emp));
    
    // Kiểm tra nếu là BGĐ thì redirect đến trang BGĐ
    if (isVIP(emp)) {
      navigate('/vip');
    }
  };

  const handleEmployeeLogout = () => {
    setEmployee(null);
    localStorage.removeItem('chamcong_employee');
    navigate('/');
  };

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
  };

  const handleNavigateToHistory = () => {
    console.log('Navigating to /history using React Router');
    navigate('/history');
  };

  // Check if employee should be blocked (employee logged in + desktop)
  const shouldBlockEmployee = employee && isDesktop;
  
  // Debug logging
  useEffect(() => {
    if (employee) {
      console.log('[App] Employee logged in:', employee.name);
      console.log('[App] isDesktop:', isDesktop);
      console.log('[App] shouldBlockEmployee:', shouldBlockEmployee);
    }
  }, [employee, isDesktop, shouldBlockEmployee]);

  return (
    <div className="app">
      <Routes>
        {/* Trang chủ: Login hoặc Attendance nếu đã login */}
        <Route 
          path="/" 
          element={
            employee ? (
              shouldBlockEmployee ? (
                <div className="app-container">
                  <header className="app-header">
                    <div className="user-info">
                      <div className="header-left">
                        <img src="/logo.png" alt="FSI Logo" className="header-logo" />
                        <span className="user-name">{getGreeting(employee.name)}</span>
                      </div>
                      <div className="header-actions">
                        <button onClick={handleEmployeeLogout} className="btn-logout">
                          Đăng xuất
                        </button>
                      </div>
                    </div>
                  </header>
                  <DesktopBlockWarning 
                    onLogout={handleEmployeeLogout}
                    showLogout={true}
                  />
                </div>
              ) : (
                <div className="app-container">
                  <header className="app-header">
                    <div className="user-info">
                      <div className="header-left">
                        <img src="/logo.png" alt="FSI Logo" className="header-logo" />
                        <span className="user-name">{getGreeting(employee.name)}</span>
                      </div>
                      <div className="header-actions">
                        <button 
                          onClick={handleNavigateToHistory}
                          className="btn-history"
                        >
                          📅 Lịch sử
                        </button>
                        <button onClick={handleEmployeeLogout} className="btn-logout">
                          Đăng xuất
                        </button>
                      </div>
                    </div>
                  </header>
                  <AttendancePage 
                    employee={employee} 
                    apiBaseUrl={process.env.REACT_APP_API_URL || ''}
                  />
                </div>
              )
            ) : (
              <LoginPage 
                onLogin={handleEmployeeLogin} 
                onAdminLogin={() => navigate('/admin-login')}
              />
            )
          } 
        />

        {/* Trang login Admin */}
        <Route 
          path="/admin-login" 
          element={
            isAdminLoggedIn ? (
              <Navigate to="/admin" replace />
            ) : (
              <AdminLogin 
                onLogin={handleAdminLogin} 
                onBack={() => navigate('/')} 
              />
            )
          } 
        />

        {/* Trang Admin Dashboard */}
        <Route 
          path="/admin" 
          element={
            isAdminLoggedIn ? (
              <AdminPage />
            ) : (
              <Navigate to="/admin-login" replace />
            )
          } 
        />

        {/* Trang VIP */}
        <Route 
          path="/vip" 
          element={
            employee ? (
              // Chỉ cho phép email VIP vào trang này
              isVIP(employee) ? (
              <div className="app-container">
                <header className="app-header">
                  <div className="user-info">
                    <div className="header-left">
                      <img src="/logo.png" alt="FSI Logo" className="header-logo" />
                      <span className="user-name">{getGreeting(employee.name)}</span>
                    </div>
                    <div className="header-actions">
                      <button 
                        onClick={handleNavigateToHistory}
                        className="btn-history"
                      >
                        📅 Lịch sử
                      </button>
                      <button onClick={handleEmployeeLogout} className="btn-logout">
                        Đăng xuất
                      </button>
                    </div>
                  </div>
                </header>
                <AttendancePageBGD 
                  employee={employee} 
                  apiBaseUrl={process.env.REACT_APP_API_URL || ''}
                />
              </div>
              ) : (
                <Navigate to="/" replace />
              )
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

        {/* Backward-compat: link cũ /bgd tự chuyển sang /vip */}
        <Route path="/bgd" element={<Navigate to="/vip" replace />} />

        {/* Trang Lịch sử chấm công */}
        <Route 
          path="/history" 
          element={
            employee ? (
              shouldBlockEmployee ? (
                <div className="app-container">
                  <header className="app-header">
                    <div className="user-info">
                      <div className="header-left">
                        <img src="/logo.png" alt="FSI Logo" className="header-logo" />
                        <span className="user-name">{getGreeting(employee.name)}</span>
                      </div>
                      <div className="header-actions">
                        <button onClick={handleEmployeeLogout} className="btn-logout">
                          Đăng xuất
                        </button>
                      </div>
                    </div>
                  </header>
                  <DesktopBlockWarning 
                    onLogout={handleEmployeeLogout}
                    showLogout={true}
                  />
                </div>
              ) : (
                <div className="app-container">
                  <header className="app-header">
                    <div className="user-info">
                      <div className="header-left">
                        <img src="/logo.png" alt="FSI Logo" className="header-logo" />
                        <span className="user-name">{getGreeting(employee.name)}</span>
                      </div>
                      <div className="header-actions">
                        <button 
                          onClick={() => {
                            navigate(isVIP(employee) ? '/vip' : '/');
                          }}
                          className="btn-back-home"
                        >
                          ← Về trang chủ
                        </button>
                        <button onClick={handleEmployeeLogout} className="btn-logout">
                          Đăng xuất
                        </button>
                      </div>
                    </div>
                  </header>
                  <HistoryPage employee={employee} />
                </div>
              )
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

        {/* Redirect các link sai về trang chủ */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  const [employee, setEmployee] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  // Khôi phục trạng thái đăng nhập của nhân viên từ localStorage
  useEffect(() => {
    const savedEmployee = localStorage.getItem('chamcong_employee');
    if (savedEmployee) {
      try {
        setEmployee(JSON.parse(savedEmployee));
      } catch (e) {
        console.error('Error parsing saved employee:', e);
        localStorage.removeItem('chamcong_employee');
      }
    }
  }, []);

  return (
    <Router>
      <AppContent 
        employee={employee}
        setEmployee={setEmployee}
        isAdminLoggedIn={isAdminLoggedIn}
        setIsAdminLoggedIn={setIsAdminLoggedIn}
      />
    </Router>
  );
}

export default App;
